import React from "react";
import { Sparkles } from "lucide-react";

/**
 * Shown on Pro / Premium kits when the AI media (images and/or videos) is
 * still being prepared after delivery. Replaces the Starter-tier "shoot it
 * yourself" content so paying customers don't see DIY guidance they didn't
 * pay for.
 *
 * `tier` is "pro" | "premium". `pendingImages` / `pendingVideos` are booleans:
 *   - pendingImages = true  → 30 AI grid images not yet rendered
 *   - pendingVideos = true  → 5 cinematic reels not yet rendered
 */
export const MediaPendingBanner = ({ tier, pendingImages, pendingVideos }) => {
    if (!pendingImages && !pendingVideos) return null;
    const tierLabel = tier === "premium" ? "Premium" : "Pro";

    const items = [];
    if (pendingImages) {
        items.push({
            label: "30 IG-grid images",
            sub: "Custom-rendered to match your real product photos and brand palette.",
        });
    }
    if (pendingVideos) {
        items.push({
            label: "5 cinematic Reels",
            sub: "8-second 9:16 clips · hand-reviewed by Kaily before delivery.",
        });
    }

    return (
        <section className="mb-16" data-testid="kit-media-pending-banner">
            <div className="rounded-2xl border-2 border-cafe-terracotta/30 bg-cafe-terracotta/5 p-6 sm:p-8">
                <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cafe-terracotta text-cafe-cream text-[10px] uppercase tracking-[0.2em] font-semibold">
                        <Sparkles className="w-3 h-3" /> {tierLabel} · in studio
                    </span>
                    <span className="text-xs text-cafe-mocha">Hand-reviewed by Kaily before delivery</span>
                </div>
                <h2 className="font-serif text-2xl sm:text-3xl text-cafe-espresso leading-tight mb-3">
                    Your AI media is being{" "}
                    <em className="font-display italic text-cafe-terracotta">rendered</em>.
                </h2>
                <p className="text-cafe-mocha text-sm leading-relaxed mb-5 max-w-xl">
                    Your captions, hooks and 30-day calendar above are ready to read now. The visual assets below come next — typically delivered within a few hours of brief submission. You'll get a fresh email when each batch is signed off.
                </p>
                <ul className="space-y-2.5">
                    {items.map((it, i) => (
                        <li key={i} className="flex items-start gap-3">
                            <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-cafe-terracotta/15 text-cafe-terracotta text-[10px] font-bold">
                                {i + 1}
                            </span>
                            <div>
                                <p className="text-cafe-espresso text-sm font-medium">{it.label}</p>
                                <p className="text-cafe-mocha text-xs leading-relaxed">{it.sub}</p>
                            </div>
                        </li>
                    ))}
                </ul>
                <p className="text-[11px] text-cafe-mocha/70 italic mt-5">
                    Status auto-refreshes when you reload this page. No action needed from you.
                </p>
            </div>
        </section>
    );
};
